import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE } from './config';
import socket from '../socket/socket';
import { apiFetch } from '../utils/apiFetch';
import { invalidateAdminAlerts, loadAdminAlerts } from '../services/adminAlertsApi';

const clampPosition = (x, y, width, height) => ({
  x: Math.max(8, Math.min(window.innerWidth - width - 8, x)),
  y: Math.max(8, Math.min(window.innerHeight - height - 8, y))
});

const positionPanelBeside = (button, preferredWidth = 420, preferredHeight = 560) => {
  const rect = button.getBoundingClientRect();
  const width = Math.min(preferredWidth, window.innerWidth - 16);
  const height = Math.min(preferredHeight, window.innerWidth <= 700 ? window.innerHeight * 0.38 : window.innerHeight - 16);
  if (window.innerWidth <= 700) {
    const below = rect.bottom + 8;
    return clampPosition(8, below + height <= window.innerHeight - 8 ? below : rect.top - height - 8, width, height);
  }
  if (rect.right + 10 + width <= window.innerWidth - 8) return clampPosition(rect.right + 10, rect.top, width, height);
  if (rect.left - 10 - width >= 8) return clampPosition(rect.left - width - 10, rect.top, width, height);
  return clampPosition(rect.left, rect.bottom + 8, width, height);
};

function AdminMessageInbox({ groupId }) {
  const [alerts, setAlerts] = useState({ matchRequests: [], waitAlerts: [] });
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [seenAlertIds, setSeenAlertIds] = useState(() => new Set());
  const [buttonPosition, setButtonPosition] = useState(null);
  const [panelPosition, setPanelPosition] = useState(null);
  const dragRef = useRef(null);
  const movedRef = useRef(false);

  useEffect(() => {
    try {
      const storedIds = JSON.parse(localStorage.getItem(`admin_message_seen_${Number(groupId)}`) || '[]');
      setSeenAlertIds(new Set(Array.isArray(storedIds) ? storedIds.map(Number) : []));
    } catch {
      setSeenAlertIds(new Set());
    }
  }, [groupId]);

  const loadAlerts = useCallback(async () => {
    if (!groupId) return;
    try {
      const result = await loadAdminAlerts(groupId);
      setAlerts({ matchRequests: result.matchRequests || [], waitAlerts: result.waitAlerts || [] });
    } catch (error) {
      console.error('LOAD ADMIN MESSAGE INBOX ERROR:', error);
    }
  }, [groupId]);

  useEffect(() => {
    loadAlerts();
    const refresh = payload => {
      if ((!payload?.groupId || Number(payload.groupId) === Number(groupId)) && String(payload?.type || '').startsWith('WAIT_ALERT')) {
        invalidateAdminAlerts(groupId);
        loadAlerts();
      }
    };
    socket.on('admin-alert-updated', refresh);
    const timer = window.setInterval(loadAlerts, 30000);
    return () => {
      socket.off('admin-alert-updated', refresh);
      window.clearInterval(timer);
    };
  }, [groupId, loadAlerts]);

  useEffect(() => {
    if (!open || alerts.waitAlerts.length === 0) return;
    const visibleIds = alerts.waitAlerts.map(item => Number(item.id));
    setSeenAlertIds(previous => {
      if (visibleIds.every(id => previous.has(id))) return previous;
      const next = new Set(previous);
      visibleIds.forEach(id => next.add(id));
      localStorage.setItem(`admin_message_seen_${Number(groupId)}`, JSON.stringify([...next]));
      return next;
    });
  }, [alerts.waitAlerts, groupId, open]);

  const startDrag = (event, kind) => {
    if (kind === 'panel' && (event.target.closest('button') || !event.target.closest('header'))) return;
    const rect = event.currentTarget.getBoundingClientRect();
    movedRef.current = false;
    dragRef.current = { kind, pointerX: event.clientX, pointerY: event.clientY, left: rect.left, top: rect.top, width: rect.width, height: rect.height };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const moveDrag = event => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = event.clientX - drag.pointerX;
    const dy = event.clientY - drag.pointerY;
    if (Math.abs(dx) + Math.abs(dy) > 5) movedRef.current = true;
    const next = clampPosition(drag.left + dx, drag.top + dy, drag.width, drag.height);
    if (drag.kind === 'button') setButtonPosition(next);
    else setPanelPosition(next);
  };

  const stopDrag = event => {
    if (!dragRef.current) return;
    dragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const resolveAlert = async id => {
    setBusyId(Number(id));
    try {
      const response = await apiFetch(`${API_BASE}/api/match-planner/${Number(groupId)}/wait-alerts/${Number(id)}/resolve`, {
        method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` }
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.message || 'บันทึกรับทราบไม่สำเร็จ');
      invalidateAdminAlerts(groupId);
      await loadAlerts();
    } catch (error) { window.alert(error.message); }
    finally { setBusyId(null); }
  };

  const totalCount = alerts.waitAlerts.length;
  const count = alerts.waitAlerts.reduce((total, item) => total + (seenAlertIds.has(Number(item.id)) ? 0 : 1), 0);

  return <>
    <button
      type="button"
      className={`admin-message-float${count === 0 ? ' is-empty' : ''}`}
      style={buttonPosition ? { left: buttonPosition.x, top: buttonPosition.y, right: 'auto' } : undefined}
      onPointerDown={event => startDrag(event, 'button')}
      onPointerMove={moveDrag}
      onPointerUp={stopDrag}
      onPointerCancel={stopDrag}
      onClick={event => {
        if (movedRef.current) { movedRef.current = false; return; }
        setPanelPosition(positionPanelBeside(event.currentTarget));
        setOpen(value => !value);
      }}
      title="กดเพื่อเปิด • ลากเพื่อย้ายตำแหน่ง"
    >
      <i aria-hidden="true">🔔</i><span>กล่องข้อความ</span>{count > 0 && <b>{count}</b>}
    </button>

    {open && <aside
      className="admin-message-panel"
      style={panelPosition ? { left: panelPosition.x, top: panelPosition.y, right: 'auto' } : undefined}
      onPointerDown={event => startDrag(event, 'panel')}
      onPointerMove={moveDrag}
      onPointerUp={stopDrag}
      onPointerCancel={stopDrag}
    >
      <header><div><strong>💬 กล่องข้อความผู้เล่น</strong><small>{totalCount} รายการ • ลากหัวกล่องเพื่อย้าย</small></div><button type="button" onClick={() => setOpen(false)}>×</button></header>
      <section>
        {totalCount === 0 && <p>ยังไม่มีข้อความใหม่</p>}
        {alerts.waitAlerts.map(item => <article className="is-wait" key={`wait-${item.id}`}><div><strong>⏰ {item.requester_name}</strong><span>“{item.note || 'แจ้งว่ารอนาน'}”</span></div><button type="button" disabled={busyId === Number(item.id)} onClick={() => resolveAlert(item.id)}>{busyId === Number(item.id) ? 'กำลังบันทึก…' : '✓ รับทราบ'}</button></article>)}
      </section>
    </aside>}
  </>;
}

export default AdminMessageInbox;
